import React, { useState, useRef, useCallback } from "react";
import Icon from "../../../common/Icon";
import CostDetailsEditForm, { CostEditFormData, ApiFileData } from "./CostDetailsEditForm";
import { toast } from "react-toastify";
import { downloadS3FileSecurely } from "../../../../utils/common";

export const COST_TYPE_OPTIONS = [
  { id: 1, label: "Professional Service Fees", key: "professional_service_fees" },
  { id: 2, label: "Statutory / Government Fees", key: "statutory_government_fees" },
  { id: 3, label: "Subscription / License Fee", key: "subscription_license_fee" },
  { id: 4, label: "Documentation / Processing Charges", key: "documentation_processing_charges" },
  { id: 5, label: "Renewal Fees", key: "renewal_fees"},
  { id: 6, label: "Late Fees / Penalties", key: "late_fees_penalties" },
  { id: 7, label: "Miscellaneous", key: "miscellaneous" },
];

export interface ApiInvoice {
  id: string;
  name: string;
  file_path: string;
  asset_type: string;
  file_size: number;
  content_type: string;
  status: string;
  s3_key?: string;
  s3_bucket?: string;
  s3_region?: string;
}

export interface ApiCostDetail {
  id: string;
  cost_category: string;
  cost_description: string;
  cost_amount: string;
  currency: string;
  tax_applicable: boolean;
  tax_amount: string;
  total_cost: string;
  vendor_name: string;
  payment_status: string;
  cost_period_from: string;
  cost_period_to: string;
  documents?: ApiFileData[];
  invoices?: ApiInvoice[];
}

interface CostDetailsAccordionProps {
  costDetails: ApiCostDetail[];
  licenseId: number;
  onUpdate: (costTypeKey: string, data: CostEditFormData, originalData?: CostEditFormData) => Promise<void>;
  canEdit?: boolean;
  isLoading?: boolean;
}

const CostDetailsAccordion: React.FC<CostDetailsAccordionProps> = ({
  costDetails,
  onUpdate,
  canEdit = false,
  isLoading = false,
}) => {
  const [expandedAccordions, setExpandedAccordions] = useState<string[]>([]);
  const [editFormPopup, setEditFormPopup] = useState<{
    isOpen: boolean;
    costTypeKey: string;
    costTypeLabel: string;
  }>({
    isOpen: false,
    costTypeKey: "",
    costTypeLabel: "",
  });

  const accordionRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const setAccordionRef = useCallback((key: string) => (el: HTMLDivElement | null) => {
    accordionRefs.current[key] = el;
  }, []);

  const toggleAccordion = (costTypeKey: string) => {
    const isCollapsing = expandedAccordions.includes(costTypeKey);
    setExpandedAccordions((prev) =>
      prev.includes(costTypeKey) ? [] : [costTypeKey]
    );

    if (!isCollapsing) {
      setTimeout(() => {
        const el = accordionRefs.current[costTypeKey];
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }
      }, 350);
    }
  };

  const handleOpenEditForm = (costTypeKey: string) => {
    const costType = COST_TYPE_OPTIONS.find((ct) => ct.key === costTypeKey);
    if (costType) {
      setEditFormPopup({
        isOpen: true,
        costTypeKey: costType.key,
        costTypeLabel: costType.label,
      });
    }
  };

  const handleCloseEditForm = () => {
    setEditFormPopup({
      isOpen: false,
      costTypeKey: "",
      costTypeLabel: "",
    });
  };

  const handleSaveEditForm = async (costTypeKey: string, data: CostEditFormData) => {
    try {
      const originalData = getCurrentEditData();
      await onUpdate(costTypeKey, data, originalData);
      handleCloseEditForm();
    } catch (error) {
      // toast.error("Failed to update cost details");
      throw error;
    }
  };

  const handleDownload = async (fileUrl: string, fileName: string) => {
    if (!fileUrl) {
      toast.error("File URL not available");
      return;
    }
    try {
      await downloadS3FileSecurely(fileName, fileUrl);
    } catch (error) {
      console.error("Error downloading file:", error);
      toast.error("Failed to download file");
    }
  };

// Helper to get cost type key from cost_category label
  const getCostTypeKeyFromCategory = (category: string): string => {
    const costType = COST_TYPE_OPTIONS.find((ct) => ct.label === category);
    return costType ? costType.key : category.toLowerCase().replace(/[\s\/]+/g, "_");
  };

  // Helper to format date from ISO string for display
  const formatDate = (dateString: string): string => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "numeric" });
  };

  // Helper to format ISO date to YYYY-MM-DD for date input
  const formatDateForInput = (dateString: string): string => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Convert API cost detail to edit form data
  const getInitialEditData = (costDetail: ApiCostDetail): CostEditFormData => ({
    id: costDetail.id as any,
    cost_type_key: getCostTypeKeyFromCategory(costDetail.cost_category),
    cost_description: costDetail.cost_description || "",
    cost_amount: costDetail.cost_amount || "",
    currency: costDetail.currency || "",
    tax_applicable: costDetail.tax_applicable,
    tax_amount: costDetail.tax_amount || "",
    total_cost_including_tax: costDetail.total_cost || "",
    vendor_service_provider_name: costDetail.vendor_name || "",
    payment_status: costDetail.payment_status || "",
    cost_period_from: formatDateForInput(costDetail.cost_period_from),
    cost_period_to: formatDateForInput(costDetail.cost_period_to),
    existing_files: costDetail.documents || [],
    new_files: [],
    deleted_file_ids: [],
  });

  // Get cost detail for the current edit popup
  const getCurrentEditData = (): CostEditFormData | undefined => {
    if (!editFormPopup.costTypeKey) return undefined;
    const costDetail = costDetails.find(
      (cd) => getCostTypeKeyFromCategory(cd.cost_category) === editFormPopup.costTypeKey
    );
    return costDetail ? getInitialEditData(costDetail) : undefined;
  };

  if (!costDetails || costDetails.length === 0) {
    return null;
  }

  return (
    <>
      <div className="cost-details-accordion-section">
        <div className="fw-semibold mb-3 text-md">Cost Details:</div>

        {costDetails.map((costDetail) => {
          const costTypeKey = getCostTypeKeyFromCategory(costDetail.cost_category);
          const costType = COST_TYPE_OPTIONS.find(
            (ct) => ct.label === costDetail.cost_category
          );
          const displayLabel = costType ? costType.label : costDetail.cost_category;
          const isExpanded = expandedAccordions.includes(costTypeKey);

          return (
            <div
              key={costDetail.id}
              ref={setAccordionRef(costTypeKey)}
              className="cost-accordion-item mb-3"
              style={{ border: "1px solid #D1D5DB", borderRadius: "8px" }}
            >
              <div
                className="cost-accordion-header d-flex justify-content-between align-items-center"
                style={{
                  background: isExpanded ? "#F3F4F6" : "transparent",
                  cursor: "pointer",
                  transition: "background 0.3s ease, border-radius 0.3s ease",
                  borderRadius: isExpanded ? "8px 8px 0px 0px" : "0px",
                  padding: "10px 12px"
                }}
                onClick={() => toggleAccordion(costTypeKey)}
              >
                <span className="fw-semibold fs-6">{displayLabel}</span>
                <div className="d-flex align-items-center gap-3">
                  {canEdit && (
                    <button
                      type="button"
                      className="btn btn-link p-0 text-decoration-none d-flex align-items-center gap-1 fw-semibold text-md"
                      style={{ color: "var(--royal-blue)" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenEditForm(costTypeKey);
                      }}
                    >
                      <Icon name="edit" />
                      Edit
                    </button>
                  )}
                  <span
                    style={{
                      transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "transform 0.3s ease",
                    }}
                  >
                    <Icon name="chevrondown" />
                  </span>
                </div>
              </div>
              <div
                className="cost-accordion-body"
                style={{
                  maxHeight: isExpanded ? "2000px" : "0",
                  overflow: "hidden",
                  transition: "max-height 0.3s ease-in-out",
                  borderTop: "none",
                  borderRadius: "0 0 8px 8px",
                }}
              >
                <div className="p-3 text-md">
                  <div className="row mb-3">
                    <div className="w-50 fw-semibold">Cost Description</div>
                    <div className="w-50 fw-medium">{costDetail.cost_description || "-"}</div>
                  </div>
                  <div className="row mb-3">
                    <div className="w-50 fw-semibold">Cost Amount</div>
                    <div className="w-50 fw-medium">{costDetail.cost_amount || "-"}</div>
                  </div>
                  <div className="row mb-3">
                    <div className="w-50 fw-semibold">Tax Applicable</div>
                    <div className="w-50 fw-medium">{costDetail.tax_applicable ? "Yes" : "No"}</div>
                  </div>
                  {costDetail.tax_applicable && (
                    <div className="row mb-3">
                      <div className="w-50 fw-semibold">Tax Amount</div>
                      <div className="w-50 fw-medium">{costDetail.tax_amount || "-"}</div>
                    </div>
                  )}
                  <div className="row mb-3">
                    <div className="w-50 fw-semibold">
                      Total Cost {costDetail.tax_applicable ? "(Including Tax)" : ""}
                    </div>
                    <div className="w-50 fw-medium">{costDetail.total_cost || "-"}</div>
                  </div>
                  <div className="row mb-3">
                    <div className="w-50 fw-semibold">Vendor/Service Provider Name</div>
                    <div className="w-50 fw-medium">{costDetail.vendor_name || "-"}</div>
                  </div>
                  <div className="row mb-3">
                    <div className="w-50 fw-semibold">Payment Status</div>
                    <div className="w-50 fw-medium">{costDetail.payment_status || "-"}</div>
                  </div>
                  <div className="row mb-3">
                    <div className="w-50 fw-semibold">Cost Period</div>
                    <div className="w-50 fw-medium">
                      {costDetail.cost_period_from && costDetail.cost_period_to
                        ? `${formatDate(costDetail.cost_period_from)} to ${formatDate(costDetail.cost_period_to)}`
                        : "-"}
                    </div>
                  </div>
                  {costDetail.invoices && costDetail.invoices.length > 0 && (
                    <div className="row">
                      <div className="w-50 fw-semibold">Cost Document</div>
                      <div className="w-50 fw-medium">
                        {costDetail.invoices.map((invoice) => (
                          <div
                            key={invoice.id}
                            className="d-flex align-items-center justify-content-between gap-2 mb-1 p-2"
                            style={{
                              background: "rgba(60, 147, 216, 0.10)",
                              borderRadius: "6px",
                              border: "1px solid #E5E7EB",
                            }}
                          >
                            <span
                              className="text-sm"
                              style={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {invoice.name}
                            </span>
                            <button
                              type="button"
                              className="btn btn-link p-0"
                              style={{ color: "var(--royal-blue)", minWidth: "24px" }}
                              onClick={() => handleDownload(invoice.file_path, invoice.name)}
                              title="Download"
                            >
                              <Icon name="download" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {costDetail.documents && costDetail.documents.length > 0 && (
                    <div className="row">
                      <div className="w-50 fw-semibold">Cost Document</div>
                      <div className="w-50 fw-medium">
                        {costDetail.documents.map((file) => (
                          <div
                            key={file.id}
                            className="d-flex align-items-center justify-content-between gap-2 mb-1 p-2"
                            style={{
                              background: "rgba(60, 147, 216, 0.10)",
                              borderRadius: "6px",
                              border: "1px solid #E5E7EB",
                            }}
                          >
                            <span
                              className="text-sm"
                              style={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {file.original_filename || file.file_name}
                            </span>
                            <button
                              type="button"
                              className="btn btn-link p-0"
                              style={{ color: "var(--royal-blue)", minWidth: "24px" }}
                              onClick={() => handleDownload(file.file_path, file.original_filename || file.file_name)}
                              title="Download"
                            >
                              <Icon name="download" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Edit Form Popup */}
      <CostDetailsEditForm
        isOpen={editFormPopup.isOpen}
        costTypeLabel={editFormPopup.costTypeLabel}
        costTypeKey={editFormPopup.costTypeKey}
        onClose={handleCloseEditForm}
        onSave={handleSaveEditForm}
        initialData={getCurrentEditData()}
        isLoading={isLoading}
      />
    </>
  );
};

export default CostDetailsAccordion;
